import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Alcohol & Drugs questions
export const alcoholDrugsMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-ad-1',
    text: {
      en: 'What is the maximum blood alcohol concentration (BAC) allowed for drivers in the Netherlands?',
      nl: 'Wat is de maximaal toegestane bloedalcoholconcentratie (BAC) voor bestuurders in Nederland?',
      ar: 'ما هي الحد الأقصى المسموح به لتركيز الكحول في الدم (BAC) للسائقين في هولندا؟'
    },
    options: [
      {
        id: 'q-ad-1o1',
        text: {
          en: '0.02%',
          nl: '0,02%',
          ar: '0.02%'
        }
      },
      {
        id: 'q-ad-1o2',
        text: {
          en: '0.05%',
          nl: '0,05%',
          ar: '0.05%'
        }
      },
      {
        id: 'q-ad-1o3',
        text: {
          en: '0.08%',
          nl: '0,08%',
          ar: '0.08%'
        }
      }
    ],
    correctAnswerId: 'q-ad-1o2',
    explanation: {
      en: 'The maximum blood alcohol concentration (BAC) allowed for drivers in the Netherlands is 0.05%. This is lower than many other countries to improve road safety.',
      nl: 'De maximaal toegestane bloedalcoholconcentratie (BAC) voor bestuurders in Nederland is 0,05%. Dit is lager dan in veel andere landen om de verkeersveiligheid te verbeteren.',
      ar: 'الحد الأقصى المسموح به لتركيز الكحول في الدم (BAC) للسائقين في هولندا هو 0.05%. هذا أقل من العديد من البلدان الأخرى لتحسين سلامة الطرق.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-2',
    text: {
      en: 'What is the maximum BAC for drivers with less than 5 years of experience?',
      nl: 'Wat is de maximaal toegestane BAC voor bestuurders met minder dan 5 jaar ervaring?',
      ar: 'ما هي الحد الأقصى المسموح به لـ BAC للسائقين الذين لديهم أقل من 5 سنوات من الخبرة؟'
    },
    options: [
      {
        id: 'q-ad-2o1',
        text: {
          en: '0.02%',
          nl: '0,02%',
          ar: '0.02%'
        }
      },
      {
        id: 'q-ad-2o2',
        text: {
          en: '0.05%',
          nl: '0,05%',
          ar: '0.05%'
        }
      },
      {
        id: 'q-ad-2o3',
        text: {
          en: '0.08%',
          nl: '0,08%',
          ar: '0.08%'
        }
      }
    ],
    correctAnswerId: 'q-ad-2o1',
    explanation: {
      en: 'Drivers with less than 5 years of experience have a stricter BAC limit of 0.02%, which is essentially zero tolerance.',
      nl: 'Bestuurders met minder dan 5 jaar ervaring hebben een strengere BAC-limiet van 0,02%, wat in feite nultolerantie is.',
      ar: 'السائقون الذين لديهم أقل من 5 سنوات من الخبرة لديهم حد أقصى أكثر صرامة لـ BAC وهو 0.02%، وهو في الأساس عدم التسامح.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-3',
    text: {
      en: 'What is the penalty for driving with a BAC between 0.05% and 0.08%?',
      nl: 'Wat is de straf voor rijden met een BAC tussen 0,05% en 0,08%?',
      ar: 'ما هي العقوبة للقيادة مع BAC بين 0.05% و 0.08%؟'
    },
    options: [
      {
        id: 'q-ad-3o1',
        text: {
          en: 'Warning only',
          nl: 'Alleen een waarschuwing',
          ar: 'تحذير فقط'
        }
      },
      {
        id: 'q-ad-3o2',
        text: {
          en: 'Fine of €140',
          nl: 'Boete van €140',
          ar: 'غرامة 140 يورو'
        }
      },
      {
        id: 'q-ad-3o3',
        text: {
          en: 'Fine of €240',
          nl: 'Boete van €240',
          ar: 'غرامة 240 يورو'
        }
      }
    ],
    correctAnswerId: 'q-ad-3o3',
    explanation: {
      en: 'Driving with a BAC between 0.05% and 0.08% results in a fine of €240 and possible license suspension.',
      nl: 'Rijden met een BAC tussen 0,05% en 0,08% resulteert in een boete van €240 en mogelijke rijontzegging.',
      ar: 'القيادة مع BAC بين 0.05% و 0.08% تؤدي إلى غرامة 240 يورو وربما تعليق الترخيص.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-4',
    text: {
      en: 'What is the penalty for driving with a BAC above 0.08%?',
      nl: 'Wat is de straf voor rijden met een BAC boven 0,08%?',
      ar: 'ما هي العقوبة للقيادة مع BAC أعلى من 0.08%؟'
    },
    options: [
      {
        id: 'q-ad-4o1',
        text: {
          en: 'Fine of €240',
          nl: 'Boete van €240',
          ar: 'غرامة 240 يورو'
        }
      },
      {
        id: 'q-ad-4o2',
        text: {
          en: 'Fine of €340',
          nl: 'Boete van €340',
          ar: 'غرامة 340 يورو'
        }
      },
      {
        id: 'q-ad-4o3',
        text: {
          en: 'Criminal offense with possible imprisonment',
          nl: 'Strafbaar feit met mogelijke gevangenisstraf',
          ar: 'جريمة جنائية مع احتمال السجن'
        }
      }
    ],
    correctAnswerId: 'q-ad-4o3',
    explanation: {
      en: 'Driving with a BAC above 0.08% is a criminal offense that can result in imprisonment, heavy fines, and mandatory license suspension.',
      nl: 'Rijden met een BAC boven 0,08% is een strafbaar feit dat kan resulteren in gevangenisstraf, zware boetes en verplichte rijontzegging.',
      ar: 'القيادة مع BAC أعلى من 0.08% هي جريمة جنائية يمكن أن تؤدي إلى السجن وغرامات ثقيلة وتعليق الترخيص الإلزامي.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-5',
    text: {
      en: 'How long should you wait after drinking alcohol before driving?',
      nl: 'Hoe lang moet je wachten na het drinken van alcohol voordat je gaat rijden?',
      ar: 'كم من الوقت يجب أن تنتظر بعد شرب الكحول قبل القيادة؟'
    },
    options: [
      {
        id: 'q-ad-5o1',
        text: {
          en: '1 hour per drink',
          nl: '1 uur per drankje',
          ar: 'ساعة واحدة لكل مشروب'
        }
      },
      {
        id: 'q-ad-5o2',
        text: {
          en: '2 hours per drink',
          nl: '2 uur per drankje',
          ar: 'ساعتان لكل مشروب'
        }
      },
      {
        id: 'q-ad-5o3',
        text: {
          en: 'At least 24 hours',
          nl: 'Minstens 24 uur',
          ar: '24 ساعة على الأقل'
        }
      }
    ],
    correctAnswerId: 'q-ad-5o3',
    explanation: {
      en: 'The safest approach is to wait at least 24 hours after drinking alcohol before driving. Your judgment of when you feel sober is not reliable.',
      nl: 'De veiligste aanpak is om minstens 24 uur te wachten na het drinken van alcohol voordat je gaat rijden. Je beoordeling van wanneer je nuchter voelt is niet betrouwbaar.',
      ar: 'النهج الأكثر أمانا هو الانتظار 24 ساعة على الأقل بعد شرب الكحول قبل القيادة. حكمك على متى تشعر بالرصانة ليس موثوقا.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-6',
    text: {
      en: 'What drugs are illegal to drive under the influence of in the Netherlands?',
      nl: 'Welke drugs zijn illegaal om onder invloed van te rijden in Nederland?',
      ar: 'ما هي المخدرات غير القانونية للقيادة تحت تأثيرها في هولندا؟'
    },
    options: [
      {
        id: 'q-ad-6o1',
        text: {
          en: 'Only hard drugs like cocaine and heroin',
          nl: 'Alleen harddrugs zoals cocaïne en heroïne',
          ar: 'المخدرات القوية فقط مثل الكوكايين والهيروين'
        }
      },
      {
        id: 'q-ad-6o2',
        text: {
          en: 'Only prescription medications',
          nl: 'Alleen voorgeschreven medicijnen',
          ar: 'الأدوية الموصوفة فقط'
        }
      },
      {
        id: 'q-ad-6o3',
        text: {
          en: 'All drugs that affect driving ability',
          nl: 'Alle drugs die de rijvaardigheid beïnvloeden',
          ar: 'جميع المخدرات التي تؤثر على قدرة القيادة'
        }
      }
    ],
    correctAnswerId: 'q-ad-6o3',
    explanation: {
      en: 'It is illegal to drive under the influence of any drug that affects your driving ability, including cannabis, prescription medications, and over-the-counter drugs.',
      nl: 'Het is illegaal om onder invloed van drugs te rijden die je rijvaardigheid beïnvloeden, inclusief cannabis, voorgeschreven medicijnen en vrij verkrijgbare medicijnen.',
      ar: 'من غير القانوني القيادة تحت تأثير أي دواء يؤثر على قدرتك على القيادة، بما في ذلك القنب والأدوية الموصوفة والأدوية التي لا تستلزم وصفة طبية.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-7',
    text: {
      en: 'What should you do if you are taking medication that may affect your driving?',
      nl: 'Wat moet je doen als je medicijnen gebruikt die je rijvaardigheid kunnen beïnvloeden?',
      ar: 'ماذا يجب أن تفعل إذا كنت تتناول دواء قد يؤثر على قيادتك؟'
    },
    options: [
      {
        id: 'q-ad-7o1',
        text: {
          en: 'Drive as normal',
          nl: 'Rijd normaal',
          ar: 'اقود بشكل طبيعي'
        }
      },
      {
        id: 'q-ad-7o2',
        text: {
          en: 'Drive only during the day',
          nl: 'Rijd alleen overdag',
          ar: 'اقود فقط أثناء النهار'
        }
      },
      {
        id: 'q-ad-7o3',
        text: {
          en: 'Check with your doctor or pharmacist',
          nl: 'Vraag het aan je arts of apotheker',
          ar: 'استشر طبيبك أو الصيدلي'
        }
      }
    ],
    correctAnswerId: 'q-ad-7o3',
    explanation: {
      en: 'If you are taking medication that may affect your driving, you should check with your doctor or pharmacist about whether it is safe to drive.',
      nl: 'Als je medicijnen gebruikt die je rijvaardigheid kunnen beïnvloeden, moet je bij je arts of apotheker navragen of het veilig is om te rijden.',
      ar: 'إذا كنت تتناول دواء قد يؤثر على قيادتك، يجب عليك استشارة طبيبك أو الصيدلي حول ما إذا كان من الآمن القيادة.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-8',
    text: {
      en: 'What are the signs of drug-impaired driving?',
      nl: 'Wat zijn de tekenen van rijden onder invloed van drugs?',
      ar: 'ما هي علامات القيادة تحت تأثير المخدرات؟'
    },
    options: [
      {
        id: 'q-ad-8o1',
        text: {
          en: 'Only erratic driving behavior',
          nl: 'Alleen onregelmatig rijgedrag',
          ar: 'سلوك القيادة غير المنتظم فقط'
        }
      },
      {
        id: 'q-ad-8o2',
        text: {
          en: 'Only slow reaction times',
          nl: 'Alleen langzame reactietijden',
          ar: 'أوقات رد الفعل البطيئة فقط'
        }
      },
      {
        id: 'q-ad-8o3',
        text: {
          en: 'Erratic driving, slow reactions, and poor judgment',
          nl: 'Onregelmatig rijgedrag, langzame reacties en slecht oordeel',
          ar: 'سلوك القيادة غير المنتظم، ردود الفعل البطيئة، وضعف الحكم'
        }
      }
    ],
    correctAnswerId: 'q-ad-8o3',
    explanation: {
      en: 'Signs of drug-impaired driving include erratic driving behavior, slow reaction times, poor judgment, and difficulty maintaining lane position.',
      nl: 'Tekenen van rijden onder invloed van drugs zijn onder meer onregelmatig rijgedrag, langzame reactietijden, slecht oordeel en moeite met het behouden van de rijstrookpositie.',
      ar: 'تشمل علامات القيادة تحت تأثير المخدرات سلوك القيادة غير المنتظم، وأوقات رد الفعل البطيئة، وضعف الحكم، وصعوبة الحفاظ على موضع الممر.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-9',
    text: {
      en: 'What is the penalty for refusing a breathalyzer test?',
      nl: 'Wat is de straf voor het weigeren van een alcoholtest?',
      ar: 'ما هي العقوبة لرفض اختبار التنفس للكحول؟'
    },
    options: [
      {
        id: 'q-ad-9o1',
        text: {
          en: 'No penalty',
          nl: 'Geen straf',
          ar: 'لا توجد عقوبة'
        }
      },
      {
        id: 'q-ad-9o2',
        text: {
          en: 'Small fine',
          nl: 'Kleine boete',
          ar: 'غرامة صغيرة'
        }
      },
      {
        id: 'q-ad-9o3',
        text: {
          en: 'Same penalty as driving under the influence',
          nl: 'Dezelfde straf als rijden onder invloed',
          ar: 'نفس العقوبة مثل القيادة تحت التأثير'
        }
      }
    ],
    correctAnswerId: 'q-ad-9o3',
    explanation: {
      en: 'Refusing a breathalyzer test carries the same penalty as driving under the influence, as it is considered an admission of guilt.',
      nl: 'Het weigeren van een alcoholtest heeft dezelfde straf als rijden onder invloed, omdat het wordt beschouwd als een bekentenis van schuld.',
      ar: 'رفض اختبار التنفس للكحول يحمل نفس العقوبة مثل القيادة تحت التأثير، لأنه يعتبر اعترافا بالذنب.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-10',
    text: {
      en: 'How long does alcohol stay in your system?',
      nl: 'Hoe lang blijft alcohol in je systeem?',
      ar: 'كم من الوقت يبقى الكحول في نظامك؟'
    },
    options: [
      {
        id: 'q-ad-10o1',
        text: {
          en: '1-2 hours',
          nl: '1-2 uur',
          ar: '1-2 ساعة'
        }
      },
      {
        id: 'q-ad-10o2',
        text: {
          en: '4-6 hours',
          nl: '4-6 uur',
          ar: '4-6 ساعات'
        }
      },
      {
        id: 'q-ad-10o3',
        text: {
          en: '24-48 hours',
          nl: '24-48 uur',
          ar: '24-48 ساعة'
        }
      }
    ],
    correctAnswerId: 'q-ad-10o3',
    explanation: {
      en: 'Alcohol can stay in your system for 24-48 hours, depending on factors like body weight, metabolism, and amount consumed.',
      nl: 'Alcohol kan 24-48 uur in je systeem blijven, afhankelijk van factoren zoals lichaamsgewicht, metabolisme en hoeveelheid geconsumeerd.',
      ar: 'يمكن أن يبقى الكحول في نظامك لمدة 24-48 ساعة، اعتمادا على عوامل مثل وزن الجسم والتمثيل الغذائي والكمية المستهلكة.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-11',
    text: {
      en: 'What should you do if you suspect another driver is under the influence?',
      nl: 'Wat moet je doen als je vermoedt dat een andere bestuurder onder invloed is?',
      ar: 'ماذا يجب أن تفعل إذا كنت تشك في أن سائقا آخر تحت التأثير؟'
    },
    options: [
      {
        id: 'q-ad-11o1',
        text: {
          en: 'Confront them directly',
          nl: 'Confronteer hen direct',
          ar: 'واجههم مباشرة'
        }
      },
      {
        id: 'q-ad-11o2',
        text: {
          en: 'Call the police emergency number',
          nl: 'Bel het politienoodnummer',
          ar: 'اتصل برقم الطوارئ للشرطة'
        }
      },
      {
        id: 'q-ad-11o3',
        text: {
          en: 'Follow them to their destination',
          nl: 'Volg hen naar hun bestemming',
          ar: 'اتبعهم إلى وجهتهم'
        }
      }
    ],
    correctAnswerId: 'q-ad-11o2',
    explanation: {
      en: 'If you suspect another driver is under the influence, call the police emergency number (112) and provide the vehicle description and location.',
      nl: 'Als je vermoedt dat een andere bestuurder onder invloed is, bel het politienoodnummer (112) en geef de voertuigbeschrijving en locatie door.',
      ar: 'إذا كنت تشك في أن سائقا آخر تحت التأثير، اتصل برقم الطوارئ للشرطة (112) وقدم وصف المركبة والموقع.'
    },
    subject: 'Alcohol & Drugs'
  },
  {
    id: 'q-ad-12',
    text: {
      en: 'What is the best way to prevent drunk driving?',
      nl: 'Wat is de beste manier om dronken rijden te voorkomen?',
      ar: 'ما هي أفضل طريقة لمنع القيادة تحت تأثير الكحول؟'
    },
    options: [
      {
        id: 'q-ad-12o1',
        text: {
          en: 'Designate a sober driver',
          nl: 'Wijs een nuchtere bestuurder aan',
          ar: 'عين سائقا رصينا'
        }
      },
      {
        id: 'q-ad-12o2',
        text: {
          en: 'Take a taxi or public transport',
          nl: 'Neem een taxi of openbaar vervoer',
          ar: 'استقل سيارة أجرة أو وسائل النقل العام'
        }
      },
      {
        id: 'q-ad-12o3',
        text: {
          en: 'All of the above',
          nl: 'Alle bovenstaande',
          ar: 'كل ما سبق'
        }
      }
    ],
    correctAnswerId: 'q-ad-12o3',
    explanation: {
      en: 'The best ways to prevent drunk driving are to designate a sober driver, take a taxi or public transport, or stay overnight. Never drive after drinking.',
      nl: 'De beste manieren om dronken rijden te voorkomen zijn het aanwijzen van een nuchtere bestuurder, het nemen van een taxi of openbaar vervoer, of blijven overnachten. Rijd nooit na het drinken.',
      ar: 'أفضل الطرق لمنع القيادة تحت تأثير الكحول هي تعيين سائق رصين، أو استقلال سيارة أجرة أو وسائل النقل العام، أو البقاء ليلا. لا تقود أبدا بعد الشرب.'
    },
    subject: 'Alcohol & Drugs'
  }
];

























